import React, { useEffect, useState } from "react";
import { parseISO, isValid, format as fmt } from "date-fns";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import { 
  FaSearch, FaDownload, FaFilePdf, FaFileCsv, FaFilter, 
  FaClock, FaCheckCircle, FaExclamationCircle,
  FaCalendarAlt, FaUser, FaTag, FaRedo, FaEye, FaTruck
} from "react-icons/fa";

import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import fetchData from "../../functions/fetchData";

export default function SuppliersLedger() {
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [quickFilter, setQuickFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [totals, setTotals] = useState({ credit: 0, debit: 0, balance: 0 });

  const [formData, setFormData] = useState({
    from: "",
    to: "",
    accounttype: "",
    suppliername: "",
    supplier_id: "",
    status: "all"
  });

  const quickFilters = [
    { key: 'all', label: 'All Records', icon: FaEye, color: 'primary' },
    { key: 'pending', label: 'Pending', icon: FaClock, color: 'warning' },
    { key: 'completed', label: 'Completed', icon: FaCheckCircle, color: 'success' },
    { key: 'overdue', label: 'Overdue', icon: FaExclamationCircle, color: 'danger' }
  ];

  const accountTypes = [
    { value: '', label: 'All Account Types' },
    { value: 'Account Payable', label: '💰 Account Payable' },
    { value: 'Purchase', label: '🛒 Purchase' },
    { value: 'Cash', label: '💵 Cash' },
    { value: 'Expense', label: '💸 Expense' }
  ];

  const formatDateSafe = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "yyyy-MM-dd") : "";
  };

  const formatDisplayDate = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "dd/MM/yyyy") : "";
  };

  const resetFilters = () => {
    setFormData({
      from: "",
      to: "",
      accounttype: "",
      suppliername: "",
      supplier_id: "",
      status: "all"
    });
    setQuickFilter('all');
    setSearchTerm('');
    setData(allData);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    let currentY = 22;
    
    doc.setFontSize(18);
    doc.text('Supplier Ledger Report', 14, currentY);
    currentY += 8;
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`, 14, currentY);
    currentY += 6;
    
    // Add supplier name if selected
    if (formData.suppliername) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Supplier: ${formData.suppliername}`, 14, currentY);
      doc.setFont(undefined, 'normal');
      currentY += 6;
    }
    
    // Add date range if selected
    if (formData.from || formData.to) {
      doc.setFontSize(10);
      const dateRange = `Period: ${formData.from ? formatDisplayDate(formData.from) : 'Start'} to ${formData.to ? formatDisplayDate(formData.to) : 'End'}`;
      doc.text(dateRange, 14, currentY);
      currentY += 6;
    }
    
    const tableColumn = ["Txn ID", "Date", "A/C Type", "Description", "Debit", "Credit", "Balance"];
    const tableRows = [];

    let runningBalance = 0;
    data.forEach((row) => {
      const debit = Number(row.debit_amount) || 0;
      const credit = Number(row.credit_amount) || 0;
      runningBalance += credit - debit; // For suppliers: credit increases balance, debit decreases
      
      tableRows.push([
        row.transaction_id,
        formatDisplayDate(row.date),
        row.account_type,
        row.description,
        debit.toFixed(2),
        credit.toFixed(2),
        runningBalance.toFixed(2)
      ]);
    });

    tableRows.push([
      "TOTALS",
      "",
      "",
      "",
      totals.debit.toFixed(2),
      totals.credit.toFixed(2),
      totals.balance.toFixed(2)
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: currentY + 2,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 8 },
      didParseCell: function(data) {
        // Check if this is the totals row (last row)
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fillColor = [240, 248, 255]; // Soft blue background (Alice Blue)
          data.cell.styles.textColor = [0, 0, 0]; // Black text
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10; // Larger font size
          data.cell.styles.lineWidth = 0.5;
          data.cell.styles.lineColor = [100, 149, 237]; // Cornflower blue border
        }
      }
    });

    doc.save(`supplier_ledger_report_${fmt(new Date(), "ddMMyyyy")}.pdf`);
    toast.success("PDF exported successfully!");
  };

  const exportCSVData = [
    // Add header information rows
    ...(formData.suppliername ? [
      { 
        transaction_id: 'Supplier Ledger Report',
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: `Supplier: ${formData.suppliername}`,
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: `Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`,
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: '',
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      }
    ] : [
      { 
        transaction_id: 'Supplier Ledger Report',
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: `Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`,
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: '',
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      }
    ]),
    // Add data rows
    ...data.map((row, index) => {
      let runningBalance = 0;
      data.slice(0, index + 1).forEach(r => {
        runningBalance += (Number(r.credit_amount) || 0) - (Number(r.debit_amount) || 0);
      });
      
      return {
        transaction_id: row.transaction_id,
        date: formatDisplayDate(row.date),
        supplier_name: row.supplier_name || 'N/A',
        account_type: row.account_type,
        description: row.description,
        debit_amount: row.debit_amount,
        credit_amount: row.credit_amount,
        running_balance: runningBalance.toFixed(2)
      };
    })
  ];

  const calculateTotals = (records) => {
    const credit = records.reduce((sum, r) => sum + parseFloat(r.credit_amount || 0), 0);
    const debit = records.reduce((sum, r) => sum + parseFloat(r.debit_amount || 0), 0);
    setData(records);
    setTotals({ credit, debit, balance: credit - debit }); // For suppliers: credit - debit
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "supplier_id") {
      const selected = suppliers.find((s) => s.id.toString() === value);
      setFormData((prev) => ({
        ...prev,
        supplier_id: value,
        suppliername: selected ? selected.supplier_name : "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const getStatusBadge = (record) => {
    const balance = (Number(record.credit_amount) || 0) - (Number(record.debit_amount) || 0);
    const recordDate = parseISO(record.date);
    const today = new Date();
    const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);
    
    if (balance <= 0) {
      return <span className="badge bg-success">Completed</span>;
    } else if (daysDiff > 30) {
      return <span className="badge bg-danger">Overdue</span>;
    } else {
      return <span className="badge bg-warning text-dark">Pending</span>;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const fetched = await fetchData("supplier_ledger_entries", null, "id", {});
        setAllData(fetched);
        calculateTotals(fetched);
        
        const suppliersData = await fetchData("suppliers", null, "id", {});
        setSuppliers(suppliersData);
      } catch (e) {
        console.error("Error fetching data:", e);
        toast.error("Failed to load data");
      }
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    const filterData = () => {
      const { from, to, accounttype, supplier_id } = formData;
      let filtered = [...allData];

      // Apply search term filter
      if (searchTerm) {
        filtered = filtered.filter(r => 
          r.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.account_type?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Date range filter
      const safeFrom = formatDateSafe(from);
      const safeTo = formatDateSafe(to);
      if (safeFrom && safeTo) {
        filtered = filtered.filter((r) => {
          const d = formatDateSafe(r.date);
          return d && d >= safeFrom && d <= safeTo;
        });
      }

      // Account type filter
      if (accounttype && accounttype !== '') {
        filtered = filtered.filter((r) => r.account_type === accounttype) ;
      }

      // Supplier filter
      if (supplier_id) {
        filtered = filtered.filter((r) => r.supplier_id === parseInt(supplier_id));
      }

      // Quick filter for status
      if (quickFilter !== 'all') {
        const today = new Date();
        filtered = filtered.filter(r => {
          const balance = (Number(r.credit_amount) || 0) - (Number(r.debit_amount) || 0);
          switch (quickFilter) {
            case 'pending':
              return balance > 0;
            case 'completed':
              return balance <= 0;
            case 'overdue':
              const recordDate = parseISO(r.date);
              const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);
              return balance > 0 && daysDiff > 30;
            default:
              return true;
          }
        });
      }

      calculateTotals(filtered);
    };

    filterData();
  }, [formData, allData, quickFilter, searchTerm]);

  return (
    <Layout>
      <Header title="🏢 Supplier Ledger Management" />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="container-fluid p-4">
        {/* Summary Cards */}
        <div className="row">
          <div className="col-md-3">
            <div className="card border-0 shadow-lg" style={{ 
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', 
              borderRadius: '12px',
              minHeight: '120px'
            }}>
              <div className="card-body p-3 text-white position-relative">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Total Credit</h6>
                    <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>฿{totals.credit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</h4>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded-circle p-2">
                    <FaDownload size={20} className="text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-lg" style={{ 
              background: 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)', 
              borderRadius: '12px',
              minHeight: '120px'
            }}>
              <div className="card-body p-3 text-white position-relative">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Total Debit</h6>
                    <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>฿{totals.debit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</h4>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded-circle p-2">
                    <FaDownload size={20} className="text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-lg" style={{ 
              background: totals.balance >= 0 ? 'linear-gradient(135deg, #007bff 0%, #6610f2 100%)' : 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)', 
              borderRadius: '12px',
              minHeight: '120px'
            }}>
              <div className="card-body p-3 text-white position-relative">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Net Balance</h6>
                    <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>฿{totals.balance.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</h4>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded-circle p-2">
                    <FaDownload size={20} className="text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-lg" style={{ 
              background: 'linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)', 
              borderRadius: '12px',
              minHeight: '120px'
            }}>
              <div className="card-body p-3 text-white position-relative">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Total Records</h6>
                    <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>{data.length.toLocaleString('th-TH')}</h4>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded-circle p-2">
                    <FaEye size={20} className="text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Header with Quick Actions */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="mb-0">Supplier Ledger</h5>
                    <span className="badge text-white fs-6" style={{ background: 'linear-gradient(135deg, #6f42c1 0%, #007bff 100%)' }}>{data.length} Records</span>
                  </div>
                </div>

                {/* Search Bar with All Action Buttons */}
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="input-group">
                      <span className="input-group-text" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                        <FaSearch />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Transaction ID, Description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ borderLeft: 'none' }}
                      />
                    </div>
                  </div>
                  <div className="col-md-8 d-flex justify-content-end align-items-center gap-2 flex-wrap">
                    {/* Quick Filter Buttons */}
                    {quickFilters.map(filter => {
                      const Icon = filter.icon;
                      const gradients = {
                        primary: 'linear-gradient(135deg, #4e73df 0%, #224abe 100%)',
                        warning: 'linear-gradient(135deg, #f6c23e 0%, #dda20a 100%)',
                        success: 'linear-gradient(135deg, #1cc88a 0%, #13855c 100%)',
                        danger: 'linear-gradient(135deg, #e74a3b 0%, #be2617 100%)'
                      };
                      
                      return (
                        <button
                          key={filter.key}
                          className="btn btn-sm d-flex align-items-center text-white"
                          style={{
                            background: quickFilter === filter.key ? gradients[filter.color] : `${gradients[filter.color]}99`,
                            border: 'none',
                            height: '32px',
                            whiteSpace: 'nowrap',
                            opacity: quickFilter === filter.key ? 1 : 0.85,
                            fontWeight: quickFilter === filter.key ? '600' : '500'
                          }}
                          onClick={() => setQuickFilter(filter.key)}
                        >
                          <Icon className="me-1" />
                          {filter.label}
                        </button>
                      );
                    })}
                    
                    {/* Separator */}
                    <div className="vr mx-1" style={{ height: '30px' }}></div>
                    
                    {/* Action Buttons */}
                    <button 
                      className={`btn btn-sm d-flex align-items-center ${showFilters ? 'text-white' : 'btn-outline-secondary'}`}
                      style={{ 
                        background: showFilters ? 'linear-gradient(135deg, #5a6268 0%, #3d4248 100%)' : 'transparent',
                        border: showFilters ? 'none' : '1px solid #5a6268',
                        height: '32px',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <FaFilter className="me-1" />
                      Filters
                    </button>
                    
                    <button 
                      className="btn btn-sm text-white d-flex align-items-center" 
                      style={{ 
                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', 
                        border: 'none',
                        height: '32px',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={exportToPDF}
                    >
                      <FaFilePdf className="me-1" />
                      PDF
                    </button>
                    
                    <CSVLink
                      data={exportCSVData}
                      filename={`supplier_ledger_report_${fmt(new Date(), "ddMMyyyy")}.csv`}
                      className="btn btn-sm text-white d-flex align-items-center"
                      style={{ 
                        background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', 
                        border: 'none', 
                        textDecoration: 'none',
                        height: '32px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <FaFileCsv className="me-1" />
                      CSV
                    </CSVLink>
                    
                    <button 
                      className="btn btn-sm text-white d-flex align-items-center" 
                      style={{ 
                        background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)', 
                        border: 'none',
                        height: '32px',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={resetFilters}
                    >
                      <FaRedo className="me-1" />
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light">
                  <h6 className="mb-0">
                    <FaFilter className="me-2" />
                    Advanced Filters
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">
                        <FaCalendarAlt className="me-1" />
                        From Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={formData.from}
                        onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                      />
                    </div>
                    
                    <div className="col-md-3">
                      <label className="form-label">
                        <FaCalendarAlt className="me-1" />
                        To Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={formData.to}
                        onChange={(e) => setFormData({ ...formData, to: e.target.value })}
                      />
                    </div>
                    
                    <div className="col-md-3">
                      <label className="form-label">
                        <FaTag className="me-1" />
                        Account Type
                      </label>
                      <select
                        className="form-control form-control-sm"
                        value={formData.accounttype}
                        onChange={(e) => setFormData({ ...formData, accounttype: e.target.value })}
                      >
                        {accountTypes.map((type, idx) => (
                          <option key={idx} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-3">
                      <label className="form-label">
                        <FaTruck className="me-1" />
                        Supplier
                      </label>
                      <select
                        className="form-control form-control-sm"
                        value={formData.supplier_id}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedSupplier = suppliers.find(s => s.id === parseInt(selectedId));
                          setFormData({
                            ...formData,
                            suppliername: selectedSupplier?.supplier_name || "",
                            supplier_id: selectedId,
                          });
                        }}
                      >
                        <option value="">All Suppliers</option>
                        {suppliers.map((supplier, idx) => (
                          <option key={idx} value={supplier.id}>{supplier.supplier_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Supplier Ledger Entries</h6>
                <small className="text-muted">{data.length} entries found</small>
              </div>
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : data.length === 0 ? (
                  <div className="text-center p-4">
                    <p className="text-muted mb-0">No supplier ledger entries found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover mb-0">
                      <thead className="table-dark">
                        <tr>
                          <th style={{ fontSize: '11px' }}>Transaction ID</th>
                          <th style={{ fontSize: '11px' }}>Date</th>
                          <th style={{ fontSize: '11px' }}>Account Type</th>
                          <th style={{ fontSize: '11px' }}>Description</th>
                          <th style={{ fontSize: '11px' }} className="text-end">Debit</th>
                          <th style={{ fontSize: '11px' }} className="text-end">Credit</th>
                          <th style={{ fontSize: '11px' }} className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((record, index) => (
                          <tr key={record.id || index}>
                            <td style={{ fontSize: '10px' }}>{record.transaction_id}</td>
                            <td style={{ fontSize: '10px' }}>{formatDisplayDate(record.date)}</td>
                            <td style={{ fontSize: '10px' }}>
                              <span className="badge bg-secondary">{record.account_type}</span>
                            </td>
                            <td style={{ fontSize: '10px' }}>{record.description}</td>
                            <td style={{ fontSize: '10px' }} className="text-end">
                              ฿{Number(record.debit_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                            </td>
                            <td style={{ fontSize: '10px' }} className="text-end">
                              ฿{Number(record.credit_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                            </td>
                            <td style={{ fontSize: '10px' }} className="text-center">
                              {getStatusBadge(record)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-info">
                        <tr>
                          <th colSpan="4" className="text-end" style={{ fontSize: '11px' }}>TOTALS:</th>
                          <th className="text-end" style={{ fontSize: '11px' }}>
                            ฿{totals.debit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                          </th>
                          <th className="text-end" style={{ fontSize: '11px' }}>
                            ฿{totals.credit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                          </th>
                          <th className="text-center" style={{ fontSize: '11px' }}>
                            <strong>Balance: ฿{totals.balance.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong>
                          </th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}